import { useState } from 'react';
import { IconDeviceFloppy, IconLink, IconLinkOff, IconPlus, IconTrash, IconCheck, IconMail } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import { Button, Input, Modal, Select, useToast } from '@hana/ui';
import { RCP_EXTRA, RCP_LINKABLE, rcpLinked, type RcpRow } from './rcp';
import { MailModal } from './MailModal';
import './rcpmodal.i18n';
import s from './ar.module.css';

// Mint-AR › MODAL "Phiếu thu (xem / sửa / link debit)" + CONFIRM "Xoá phiếu thu". TIỀN → số tiền che khi không có quyền `money`. Chỉ đọc: Lưu / Xoá / Gửi xác nhận báo toast.
const nf = (x: number): string => x.toLocaleString('vi-VN', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export function RcpModal({ row, money, me, onClose, onApprove }: { row: RcpRow | null; money: boolean; me: string; onClose: () => void; onApprove: (no: string, v: boolean) => void }) {
  return row ? <Body key={row.no} row={row} money={money} me={me} onClose={onClose} onApprove={onApprove} /> : null;
}

function Body({ row, money, me, onClose, onApprove }: { row: RcpRow; money: boolean; me: string; onClose: () => void; onApprove: (no: string, v: boolean) => void }) {
  const toast = useToast();
  const t = useT();
  const ex = RCP_EXTRA[row.no] ?? ['', '', ''];
  const ro = row.ap;
  const [amt, setAmt] = useState(money ? nf(row.amt) : '•••••');
  const [type, setType] = useState<string>(row.type);
  const [br, setBr] = useState(row.branch);
  const [links, setLinks] = useState(row.links);
  const [ask, setAsk] = useState(false);
  const [mail, setMail] = useState(false);
  const noWrite = () => toast({ msg: t('rm.noWrite') });
  const self = !row.ap && row.by === me;
  const adds = RCP_LINKABLE.filter((x) => !links.some((l) => l[0] === x));
  const linked = rcpLinked({ ...row, links });

  return (
    <>
      <Modal
        open={!ask && !mail}
        onOpenChange={(o) => !o && onClose()}
        title={t('rm.title', { no: row.no })}
        description={t('rm.desc', { cus: row.cus, bank: row.bank, by: row.by })}
        footer={
          <>
            <Button icon={<IconTrash size={16} aria-hidden="true" />} disabled={ro} title={ro ? t('rm.unapproveFirst') : undefined} onClick={() => setAsk(true)}>{t('rm.del')}</Button>
            <span className={s.grow} />
            <Button icon={<IconMail size={16} aria-hidden="true" />} onClick={() => setMail(true)}>{t('rm.mail')}</Button>
            <Button icon={<IconCheck size={16} aria-hidden="true" />} disabled={self} title={self ? t('rm.selfNoApprove') : undefined} onClick={() => onApprove(row.no, !row.ap)}>{row.ap ? t('rm.unapprove') : t('rm.approve')}</Button>
            <Button variant="primary" icon={<IconDeviceFloppy size={16} aria-hidden="true" />} onClick={() => { onClose(); toast({ msg: ro ? t('rm.noChange') : t('rm.saved', { no: row.no }) }); }}>{t('rm.save')}</Button>
          </>
        }
      >
        <div className={s.rcM}>
          <span className={`${s.flag} ${row.ap ? s.fOk : s.fWarn}`}><i />{row.ap ? t('rm.approved') : t('rm.pending')}</span>
          <Input label={t('rm.amt')} inputMode="decimal" value={amt} readOnly={ro || !money} onChange={(e) => setAmt(e.target.value)} />
          <Input label={t('rm.date')} value={row.date} readOnly />
          <Input label={t('rm.memo')} value={row.memo} readOnly />
          <Select label={t('rm.type')} value={type} disabled={ro} onChange={(e) => setType(e.target.value)} options={[{ value: 'inv', label: t('rm.typeInv') }, { value: 'cash', label: t('rm.typeCash') }]} />
          <Select label={t('rm.branch')} value={br} disabled={ro} onChange={(e) => setBr(e.target.value)} options={['SGN', 'HAN', 'HPH']} />
          <Input label={t('rm.payer')} defaultValue={ex[0]} readOnly={ro} />
          <Input label={t('rm.noteCus')} defaultValue={ex[1]} readOnly={ro} />
          <Input label={t('rm.noteInt')} defaultValue={ex[2]} readOnly={ro} />
          <p className={s.lb}>{row.ap ? t('rm.apBy', { by: row.apBy || 'LIENNTK', creator: row.by }) : t('rm.waitBy', { by: row.by })} · {t('rm.notSent')}</p>
          <h3 className={s.rcH}>{t('rm.link')}{money ? ` · ${nf(linked)} / ${nf(row.amt)} tr` : ''}</h3>
          {links.length === 0 && <p className={s.lb}>{t('rm.noLink')}</p>}
          {links.map((l) => (
            <div key={l[0]} className={s.rcLink}>
              <b>{l[0]}</b><span className={s.lb}>{l[1]}</span><b>{money ? `${nf(l[2])} tr` : '•••••'}</b>
              <button type="button" disabled={ro} title={ro ? t('rm.unlockLink') : t('rm.dropLink')} onClick={() => { setLinks(links.filter((x) => x[0] !== l[0])); toast({ msg: t('rm.dropped', { x: l[0], no: row.no }) }); }}><IconLinkOff size={16} aria-hidden="true" />{t('rm.drop')}</button>
            </div>
          ))}
          <div className={s.rcLink}>
            <span className={s.lb}>{t('rm.addLink')}</span>
            {adds.map((x) => (
              <button key={x} type="button" disabled={ro} onClick={() => { setLinks([...links, [x, 'SE260602010', 18.0]]); toast({ msg: t('rm.linked', { x, no: row.no }) }); }}><IconPlus size={16} aria-hidden="true" />{x}</button>
            ))}
          </div>
          {ro && <p className={s.lb}><IconLink size={14} aria-hidden="true" /> {t('rm.roHint')}</p>}
        </div>
      </Modal>
      <Modal
        open={ask}
        danger
        onOpenChange={(o) => !o && setAsk(false)}
        title={t('rm.delTitle', { no: row.no })}
        description={t('rm.delDesc')}
        footer={<><Button onClick={() => setAsk(false)}>{t('rm.keep')}</Button><Button variant="danger" icon={<IconTrash size={16} aria-hidden="true" />} onClick={() => { setAsk(false); onClose(); noWrite(); }}>{t('rm.del')}</Button></>}
      />
      <MailModal row={mail ? row : null} onClose={() => setMail(false)} />
    </>
  );
}
